import "../../styles/planner.css";
import React, { useCallback, useMemo } from "react";
import dayjs from "dayjs";
import { RenderTerminalCallLock, textColorHandler } from "../Helpers.tsx";
import { useDispatch } from "react-redux";
import { showModal } from "../../data/modal.tsx";
import { registerModalCallback } from "../../data/modalCallbacks";
import LabelDots from "./LabelDots.tsx";
import { useTranslation } from "react-i18next";
import { updateSelectedTerminalCall } from "../../data/selectedterminalcalls.js";

import {
  Box,
  IconButton,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Tooltip,
} from "@mui/material";

import {
  Comment as CommentIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

const DEFAULT_CONTAINER_COLOR = "#FFE4C4";

const hexToRgba = (hex?: string | null, opacity = 0.12) => {
  if (!hex) {
    return `rgba(255, 228, 196, ${opacity})`;
  }

  const normalizedHex = hex.replace("#", "");

  if (![3, 6].includes(normalizedHex.length)) {
    return `rgba(255, 228, 196, ${opacity})`;
  }

  const fullHex =
    normalizedHex.length === 3
      ? normalizedHex
          .split("")
          .map((char) => char + char)
          .join("")
      : normalizedHex;

  const red = parseInt(fullHex.slice(0, 2), 16);
  const green = parseInt(fullHex.slice(2, 4), 16);
  const blue = parseInt(fullHex.slice(4, 6), 16);

  if ([red, green, blue].some((value) => Number.isNaN(value))) {
    return `rgba(255, 228, 196, ${opacity})`;
  }

  return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
};

type TerminalCallDateTimeType =
  | "planned"
  | "arrival"
  | "operationStart"
  | "operationEnd"
  | "departure";

type TerminalCallDetailProps = {
  terminalCall: any;
  terminals?: any[];
  vessels?: any[];
  labels?: any[];
  voyages?: any[];
  containers?: any[];
  callback?: (data?: any) => Promise<void> | void;
};

export default function TerminalCallDetail({
  terminalCall,
  terminals,
  vessels,
  labels,
  voyages,
  containers,
  callback,
}: TerminalCallDetailProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const terminalColor = useMemo(() => {
    return terminalCall?.terminal?.color || DEFAULT_CONTAINER_COLOR;
  }, [terminalCall]);

  const terminalSoftBackground = useMemo(() => {
    return hexToRgba(terminalColor, 0.12);
  }, [terminalColor]);

  const terminalHoverBackground = useMemo(() => {
    return hexToRgba(terminalColor, 0.18);
  }, [terminalColor]);

  const openModal = useCallback(
    (opts: any) => dispatch(showModal(opts)),
    [dispatch]
  );

  const getCallbackId = useCallback(() => {
    if (!callback) {
      return undefined;
    }

    return registerModalCallback(callback);
  }, [callback]);

  const handleUnsetDateTime = useCallback(
    async (type: TerminalCallDateTimeType) => {
      if (type === "planned") {
        return;
      }

      const payload: any = {
        ...terminalCall,
        [type]: null,
      };

      /* if (type === "departure") {
        payload.arrival = null;
        payload.operationStart = null;
        payload.operationEnd = null;
      } */

      await dispatch(updateSelectedTerminalCall(payload) as any);

      if (callback) {
        callback();
      }
    },
    [dispatch, terminalCall, callback]
  );

  const openDateTimeModal = useCallback(
    ({
      type,
      title,
      description,
    }: {
      type: TerminalCallDateTimeType;
      title: string;
      description: string;
    }) => {
      openModal({
        title,
        description,
        form: "terminalcalldatetime",
        formProps: {
          terminalCall,
          type,
          title,
          callbackId: getCallbackId(),
        },
        size: "compact",
      });
    },
    [openModal, terminalCall, getCallbackId]
  );

  const openTerminalCallDetailsModal = useCallback(() => {
    openModal({
      title: t("terminalCallDetails") || t("planning"),
      description: t("terminalCallDetails") || t("planning"),
      form: "plannerterminalcalldetails",
      size: "wide",
      formProps: {
        terminalCall,
        terminals,
        vessels,
        labels,
        voyages,
        containers,
        callbackId: getCallbackId(),
      },
    });
  }, [
    openModal,
    t,
    terminalCall,
    terminals,
    vessels,
    labels,
    voyages,
    containers,
    getCallbackId,
  ]);

  const actionButtonSx = {
    width: 30,
    height: 30,
    backgroundColor: "rgba(255, 255, 255, 0.75)",
    border: "1px solid",
    borderColor: "divider",
    "&:hover": {
      backgroundColor: "background.paper",
      borderColor: terminalColor,
    },
  };

  const dateActionButtonSx = {
    width: 28,
    height: 28,
  };

  const clearDateButtonSx = {
    width: 28,
    height: 28,
    color: "error.main",
    "&:hover": {
      backgroundColor: "rgba(211, 47, 47, 0.08)",
    },
  };

  const formatTerminalCallDateTime = (value: string) => {
    const date = dayjs(value);
    const currentYear = dayjs().year();

    return date.year() === currentYear
      ? date.format("DD/MM HH:mm")
      : date.format("DD/MM/YYYY HH:mm");
  };

  const renderDateTimeRow = ({
    type,
    label,
    value,
    modalTitle,
    modalDescription,
    canUnset = true,
  }: {
    type: TerminalCallDateTimeType;
    label: string;
    value?: string | null;
    modalTitle: string;
    modalDescription: string;
    canUnset?: boolean;
  }) => {
    if (!value) {
      return null;
    }

    return (
      <TableRow>
        <TableCell>{label}:</TableCell>

        <TableCell>{formatTerminalCallDateTime(value)}</TableCell>

        <TableCell align="right">
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 0.25,
            }}
          >
            <Tooltip title={modalTitle} arrow>
              <IconButton
                size="small"
                sx={dateActionButtonSx}
                onClick={() =>
                  openDateTimeModal({
                    type,
                    title: modalTitle,
                    description: modalDescription,
                  })
                }
              >
                <ScheduleIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            {canUnset ? (
              <Tooltip title={t("clear")} arrow>
                <IconButton
                  size="small"
                  sx={clearDateButtonSx}
                  onClick={() => handleUnsetDateTime(type)}
                >
                  <CloseIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Grid
      container
      sx={{
        borderRadius: 1,
        overflow: "hidden",
        backgroundColor: "background.paper",
        boxShadow: "0 2px 10px rgba(15, 23, 42, 0.06)",
      }}
    >
      <Grid
        container
        flexWrap="nowrap"
        sx={{
          px: 1.25,
          py: 1,
          background: `linear-gradient(90deg, ${terminalSoftBackground} 0%, rgba(255,255,255,0.92) 72%)`,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Grid container alignItems="center" sx={{ minWidth: 0 }}>
          {RenderTerminalCallLock(terminalCall)}

          <Typography
            variant="subtitle1"
            fontWeight={800}
            noWrap
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              minWidth: 0,
              color: "text.primary",
            }}
          >
            <Box
              component="span"
              sx={{
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {terminalCall.terminal?.name ?? ""}
            </Box>

            <LabelDots labels={terminalCall.labels} />
          </Typography>
        </Grid>

        <Grid container justifyContent="flex-end" flexWrap="nowrap" gap={0.5}>
         {/*  <IconButton
            size="small"
            sx={actionButtonSx}
            onClick={() => {
              openModal({
                title: t("editFormTitleByType", { type: t("planning") }),
                description: t("createPlanning"),
                form: "terminalCall",
                formProps: {
                  terminalCall,
                  terminals,
                  vessels,
                  labels,
                  voyages,
                  callbackId: getCallbackId(),
                },
              });
            }}
          >
            <EditIcon sx={{ fontSize: 18 }} />
          </IconButton>

          <IconButton
            size="small"
            sx={actionButtonSx}
            onClick={() =>
              openModal({
                title: t("planningNotes"),
                description: t("planningNotes"),
                form: "note",
                formProps: {
                  terminalCall,
                  callbackId: getCallbackId(),
                },
              })
            }
          >
            <CommentIcon
              sx={{ fontSize: 18 }}
              color={terminalCall.notes?.length > 0 ? "primary" : "inherit"}
            />
          </IconButton> */}

          <Tooltip title={t("terminalCallDetails") || t("showDetails")} arrow>
            <IconButton
              size="small"
              sx={{
                ...actionButtonSx,
                color: "primary.main",
              }}
              onClick={openTerminalCallDetailsModal}
            >
              <InfoIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>

      <TableContainer
        sx={{
          backgroundColor: "background.paper",
          "& .MuiTableCell-root": {
            borderBottomColor: "divider",
          },
          "& .MuiTableRow-root:hover": {
            backgroundColor: terminalHoverBackground,
          },
        }}
      >
        <Table aria-label="terminal table" stickyHeader size="small">
          <TableBody>
            {renderDateTimeRow({
              type: "planned",
              label: t("planned"),
              value: terminalCall.planned,
              modalTitle: t("modifyPlannedDateTime"),
              modalDescription: t("modifyPlannedDateTime"),
              canUnset: false,
            })}

            {renderDateTimeRow({
              type: "arrival",
              label: t("arrival"),
              value: terminalCall.arrival,
              modalTitle: t("modifyArrivalDateTime"),
              modalDescription: t("modifyArrivalDateTime"),
            })}

            {renderDateTimeRow({
              type: "operationStart",
              label: t("startOperation"),
              value: terminalCall.operationStart,
              modalTitle: t("modifyStartOperationDateTime"),
              modalDescription: t("modifyStartOperationDateTime"),
            })}

            {renderDateTimeRow({
              type: "operationEnd",
              label: t("endOperation"),
              value: terminalCall.operationEnd,
              modalTitle: t("modifyEndOperationDateTime"),
              modalDescription: t("modifyEndOperationDateTime"),
            })}

            {renderDateTimeRow({
              type: "departure",
              label: t("departure"),
              value: terminalCall.departure,
              modalTitle: t("modifyDepartureDateTime"),
              modalDescription: t("modifyDepartureDateTime"),
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
}